/**
 * `confirmSection` — the single entry point a section's confirm control
 * calls on explicit customer activation. Wires together the write latch
 * (`state/working-copy-client.ts`), the three-member write outcome
 * (`state/outcome.ts`), and Content Design's Failed/Indeterminate wording
 * (`./outcome-content.ts`) into one result a UI component renders.
 *
 * This module holds no form state and touches no on-screen answer — it
 * takes `answers` as a plain, un-mutated argument and never reads or writes
 * it after handing it to the port. That is what makes ADR-20260826-94qx's
 * "every entered answer remains on screen" true regardless of outcome: the
 * caller's own form state is the only place those answers ever lived, and
 * nothing in this call chain (this module, the latch, or the port) offers
 * a value that could replace it. On Succeeded the caller may treat the
 * section as confirmed; on Failed or Indeterminate the caller changes
 * NOTHING about the answers it is already showing — it only additionally
 * renders the returned `title`/`message` next to the SAME confirm control
 * the customer already used (there is no second action in this result to
 * wire up).
 *
 * `actionLabel` (m1-portal-ux-fixes Fix C) is the calling screen's own
 * primary action control's visible text (e.g. "Continue") — threaded
 * straight through to `outcome-content.ts`'s `buildSaveOutcomeContent`,
 * never hardcoded here, since different screens that call this function
 * name their own control differently.
 *
 * `tone` is passed through unchanged from `outcome-content.ts` so a
 * consuming component can render it directly as the H2 square panel's
 * `tone` prop (`PortalPanelBody`, design-system) — always `"error"` for
 * this pair (H2 has no warning tone, see `outcome-content.ts`'s own doc
 * comment).
 *
 * `RenewalSubmitted` (m1-portal-ux-fixes Fix D, DEBT-20261023-a7zv): a
 * Failed outcome whose `detail.taxonomyCode` is a write-refusal naming an
 * already-submitted renewal (`isRenewalSubmittedWriteRefusal`,
 * `state/outcome.ts`) is never shown as an ordinary save failure — Epic's
 * state (submitted) has already moved past what this write was trying to
 * do, so the caller routes the session to the read-only `/renewal-status`
 * view instead. This is a FOURTH member of `ConfirmSectionResult`, this
 * module's own feature-level result type — NOT a fourth member of the
 * shared `WriteOutcome` union, which ADR-20260821-kp45 fixes at three.
 */
import { assertUnreachable, isRenewalSubmittedWriteRefusal } from "../../state/outcome";
import { saveSection } from "../../state/working-copy-client";
import type { WorkingCopyPort, WorkingCopyWriteResult } from "../../state/working-copy-port";
import { buildSaveOutcomeContent } from "./outcome-content";

export type ConfirmSectionResult =
  | { status: "Succeeded"; value: WorkingCopyWriteResult }
  | { status: "Failed"; tone: "error"; title: string; message: string }
  | { status: "Indeterminate"; tone: "error"; title: string; message: string }
  | { status: "RenewalSubmitted" };

/**
 * Confirms one section: exactly one write per call, via the latch in
 * `working-copy-client.ts` (two calls in quick succession for the same
 * renewalId/sectionId share one request — see that module). No debounce,
 * no autosave, no timer, no background write (ADR-20260821-5wwj) — this
 * function issues the write synchronously on being called, nothing more.
 */
export async function confirmSection(
  port: WorkingCopyPort,
  renewalId: string,
  sectionId: string,
  answers: Record<string, unknown>,
  actionLabel: string,
): Promise<ConfirmSectionResult> {
  const outcome = await saveSection(port, renewalId, sectionId, answers);
  const content = buildSaveOutcomeContent(actionLabel);

  switch (outcome.status) {
    case "Succeeded":
      return { status: "Succeeded", value: outcome.value };
    case "Failed":
      if (isRenewalSubmittedWriteRefusal(outcome.detail.taxonomyCode)) {
        return { status: "RenewalSubmitted" };
      }
      return {
        status: "Failed",
        tone: content.Failed.tone,
        title: content.Failed.title,
        message: content.Failed.message,
      };
    case "Indeterminate":
      return {
        status: "Indeterminate",
        tone: content.Indeterminate.tone,
        title: content.Indeterminate.title,
        message: content.Indeterminate.message,
      };
    default:
      return assertUnreachable(outcome);
  }
}
